'use es6';

import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import ContentRoutes from 'ContentUtils/Routes';
import { Referrers } from 'page-creation-lib/constants/Referrers';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { undoModuleReducerChange, redoModuleReducerChange } from 'ContentEditorUI/redux/actions/undoRedoActions';
import { getContentModelId, getSubcategory, getTemplatePath } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getThemePath } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { getHasThemeSettings } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { getHasDesignTabPermissions } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
import { getCategoryId, getIsBlogListingPage, getIsSitePage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getHasLandingPagesMultiLangAccess, getHasSitePagesMultiLangAccess, getCanUseQuickSpot, getHasAutotranslateAccess, getHasContentRemixWriteAccess, getIsStagingV2 } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getCanRedoModules, getCanUndoModules } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getCanMABExperiment } from 'page-editor-ui/redux/selectors/mabExperimentSelectors';
import { getMABCreatableMap } from 'ContentEditorUI/redux/selectors/mabExperimentSelectors';
import { updatePatagoniaSettingsModalActiveNavItem } from 'ContentEditorUI/redux/actions/baseSettingsActions';
import { getPortalHasABTesting } from 'ContentEditorUI/redux/selectors/portalSelectors';
import { requestSucceeded } from 'redux-request-state/util/requestStateCheckers';
import { getPortalScopesRequest } from 'ContentEditorUI/redux/selectors/requestStatesSelectors';
import { getHasReachedMlangPagesLimit } from 'page-editor-ui/redux/selectors/pageLimitSelectors';
import { getIsAnyTestRunning } from 'ContentEditorUI/redux/selectors/mabExperimentSelectors';
import { getShareablePreviewUrl } from 'page-editor-ui/redux/selectors/urlSelectors';
import { openModal, toggleRunTestModal, setTestType } from 'ContentEditorUI/redux/actions/appActions';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { MODAL_TYPES, TEST_MODAL_TYPES, NO_TEST_ACCESS, SMART_CONTENT } from 'ContentEditorUI/lib/modalConstants';
import useFetchTheme from 'ContentEditorUI/hooks/useFetchTheme';
import { getThemeName } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { getHasAdvancedTemplateAccessForThisPageType, getIsStarterPageTemplate } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
import { getCanSetPageLanguage } from 'page-editor-ui/redux/selectors/pageSettingsSelectors';
import useSetSettingsModalOpenWithNavItem from 'page-editor-ui/hooks/useSetSettingsModalOpenWithNavItem';
import { getPortalHasMembershipsAccess, getHasPasswordProtectionAccess, getUserHasManagePrivateContentAccess } from 'page-editor-ui/redux/selectors/authSelectors';
import { getContentModelContentTypeCategoryId, getIsPersonalized, getIsUsingPersonalization } from 'page-editor-ui/redux/selectors/pageContentModelSelectors';
import { setActivePatagoniaPanel, openPatagoniaSidebar } from 'ContentEditorUI/redux/actions/panelActions';
import { SIDEBAR_PANELS } from 'ContentEditorUI/lib/sidebar/constants';
import { useDeepLSupportedLanguages } from 'ContentComponents/hooks/useDeepLSupportedLanguages';
import { getContentAccessControlEditPermissionsDisabledTooltipI18nKey } from 'ui-memberships-settings-lib/utils/tooltips';
import { CategoryIdToAccessControlContentType } from 'ui-memberships-settings-lib/constants/ContentTypes';
import { getActiveStagingDomain } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
import { getHasStagingDomain } from 'ContentEditorUI/redux/selectors/contentSchemaSelectors';
import Categories from 'ContentUtils/constants/Categories';
import { getCanUseAutotranslateForContentLanguage } from '../redux/selectors/pageSettingsSelectors';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { Flex } from 'foundations-components/components/Flex';
import PortalIdParser from 'PortalIdParser';
import useContentEditorCreateVariantAction from 'personalization-lib/hooks/variantCreation/useContentEditorCreateVariantAction';
import { useEmbeddedEditorContext } from 'campaigns-lib/embedded/EmbeddedEditorContext';
import DisabledInStudioMessage from 'campaigns-lib/embedded/DisabledInStudioMessage';
import { ArtificialIntelligenceIcon } from 'foundations-assets/icons/components/ArtificialIntelligenceIcon';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { DuplicateIcon } from 'foundations-assets/icons/components/DuplicateIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { LanguageIcon } from 'foundations-assets/icons/components/LanguageIcon';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { QuestionIcon } from 'foundations-assets/icons/components/QuestionIcon';
import { RedoIcon } from 'foundations-assets/icons/components/RedoIcon';
import { SettingsIcon } from 'foundations-assets/icons/components/SettingsIcon';
import { TestIcon } from 'foundations-assets/icons/components/TestIcon';
import { UndoIcon } from 'foundations-assets/icons/components/UndoIcon';
import { ViewIcon } from 'foundations-assets/icons/components/ViewIcon';
import { AudienceTargetingIcon } from 'foundations-assets/icons/components/AudienceTargetingIcon';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useGetLangVariantExtraProps = () => {
  const isSitePage = useSelector(getIsSitePage);
  const isBlogListingPage = useSelector(getIsBlogListingPage);
  const hasSitePagesMultiLangAccess = useSelector(getHasSitePagesMultiLangAccess);
  const hasLandingPagesMultiLangAccess = useSelector(getHasLandingPagesMultiLangAccess);
  const activeStagingDomain = useSelector(getActiveStagingDomain);
  const isPersonalized = useSelector(getIsPersonalized);
  const hasPagesMultiLangAccess = isSitePage ? hasSitePagesMultiLangAccess : hasLandingPagesMultiLangAccess;
  const createMlangVariantExtraProps = useMemo(() => {
    // We don't allow creating a language variant while a page is staged
    const config = {
      hideLink: activeStagingDomain || isBlogListingPage
    };
    if (!hasPagesMultiLangAccess) {
      return Object.assign({}, config, {
        disableLink: true,
        tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "mLang.noAccessError"
        })
      });
    }

    // We don't allow creating a language variant if the page is a personalized variant, or has personalized variants
    if (isPersonalized) {
      return Object.assign({}, config, {
        disableLink: true,
        tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "mLang.disabledDueToPersonalization"
        })
      });
    }
    return config;
  }, [activeStagingDomain, hasPagesMultiLangAccess, isBlogListingPage, isPersonalized]);
  const directTranslateExtraProps = useMemo(() => {
    return !hasPagesMultiLangAccess ? {
      disableLink: true,
      tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "mLang.noAccessError"
      })
    } : {};
  }, [hasPagesMultiLangAccess]);
  return {
    directTranslateExtraProps,
    createMlangVariantExtraProps
  };
};
const useSettingsDropdownOptions = () => {
  const contentTypeCategoryId = useSelector(getContentModelContentTypeCategoryId);
  const hasAdvancedFeaturesScope = useSelector(getHasAdvancedTemplateAccessForThisPageType);
  const canSetPageLanguage = useSelector(getCanSetPageLanguage);
  const hasPasswordProtectionAccess = useSelector(getHasPasswordProtectionAccess);
  const portalHasMembershipsAccess = useSelector(getPortalHasMembershipsAccess);
  const userHasManagePrivateContentAccess = useSelector(getUserHasManagePrivateContentAccess);
  const isStarterPageTemplate = useSelector(getIsStarterPageTemplate);
  const isBlogListingPage = useSelector(getIsBlogListingPage);
  const isUsingPersonalization = useSelector(getIsUsingPersonalization);
  const shouldShowAudienceAccessNavItem = hasAdvancedFeaturesScope && !isStarterPageTemplate && !isBlogListingPage;
  const canViewContentAudienceAccessSettings = portalHasMembershipsAccess || hasPasswordProtectionAccess && !portalHasMembershipsAccess;
  const shouldDisableAudienceAccessNavItem = canViewContentAudienceAccessSettings && !userHasManagePrivateContentAccess || isUsingPersonalization;
  const canUseQuickSpot = useSelector(getCanUseQuickSpot);
  const setSettingsModalOpenWithNavItem = useSetSettingsModalOpenWithNavItem(MODAL_TYPES.SETTINGS_MODAL);
  const {
    openUpgradeModal: openPasswordProtectionUpgradeModal
  } = useUpgradeModal('memberships-content-password-protection');
  return useMemo(() => {
    const settingsDropdownOptions = [Object.assign({
      id: 'general',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.general"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('general')
    }, canUseQuickSpot && {
      icon: SettingsIcon,
      fullText: I18n.text('editor.header.menus.settings.generalFullDescription')
    }), Object.assign({
      id: 'image',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.image"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('image')
    }, canUseQuickSpot && {
      icon: SettingsIcon,
      fullText: I18n.text('editor.header.menus.settings.imageFullDescription')
    })];
    if (hasAdvancedFeaturesScope && !isStarterPageTemplate) {
      settingsDropdownOptions.push(Object.assign({
        id: 'template',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.settings.template"
        }),
        onClick: () => setSettingsModalOpenWithNavItem('template')
      }, canUseQuickSpot && {
        icon: SettingsIcon,
        fullText: I18n.text('editor.header.menus.settings.templateFullDescription')
      }));
    }
    if (canSetPageLanguage) {
      settingsDropdownOptions.push(Object.assign({
        id: 'langAndRegion',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.settings.langAndRegion"
        }),
        onClick: () => setSettingsModalOpenWithNavItem('langAndRegion')
      }, canUseQuickSpot && {
        icon: SettingsIcon,
        fullText: I18n.text('editor.header.menus.settings.langAndRegionFullDescription')
      }));
    }
    const handleAudienceAccessNavItemClick = () => {
      if (canViewContentAudienceAccessSettings) {
        setSettingsModalOpenWithNavItem('roleAndAccess');
      } else {
        openPasswordProtectionUpgradeModal();
      }
    };
    if (shouldShowAudienceAccessNavItem) {
      const getAudienceAccessTooltipLabel = () => {
        if (!shouldDisableAudienceAccessNavItem) {
          return null;
        }
        if (isUsingPersonalization) {
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pageSettings.patagoniaSettingsModal.roleAndAccess.audienceAccessDisabledForPersonalization"
          });
        }
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: getContentAccessControlEditPermissionsDisabledTooltipI18nKey(CategoryIdToAccessControlContentType[contentTypeCategoryId])
        });
      };
      settingsDropdownOptions.push(Object.assign({
        id: 'roleAndAccess',
        text: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "editor.header.menus.settings.roleAndAccess"
          }), !canViewContentAudienceAccessSettings && /*#__PURE__*/_jsx(LockBadge, {
            paddingInlineStart: "200"
          })]
        }),
        onClick: handleAudienceAccessNavItemClick,
        hideLink: !shouldShowAudienceAccessNavItem,
        disableLink: shouldDisableAudienceAccessNavItem,
        tooltipLabel: getAudienceAccessTooltipLabel()
      }, canUseQuickSpot && {
        icon: canViewContentAudienceAccessSettings ? SettingsIcon : LockedIcon,
        fullText: I18n.text('editor.header.menus.settings.roleAndAccessFullDescription')
      }));
    }
    if (hasAdvancedFeaturesScope) {
      settingsDropdownOptions.push(Object.assign({
        id: 'advanced',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.settings.advanced"
        }),
        onClick: () => setSettingsModalOpenWithNavItem('advanced')
      }, canUseQuickSpot && {
        icon: SettingsIcon,
        fullText: I18n.text('editor.header.menus.settings.advancedFullDescription')
      }));
    }
    return settingsDropdownOptions;
  }, [canUseQuickSpot, hasAdvancedFeaturesScope, isStarterPageTemplate, canSetPageLanguage, shouldShowAudienceAccessNavItem, setSettingsModalOpenWithNavItem, canViewContentAudienceAccessSettings, openPasswordProtectionUpgradeModal, shouldDisableAudienceAccessNavItem, contentTypeCategoryId, isUsingPersonalization]);
};
export const useNavigationGroups = () => {
  const dispatch = useDispatch();
  const {
    isInsideMarketingStudio
  } = useEmbeddedEditorContext();
  useFetchTheme();
  const templatePath = useSelector(getTemplatePath);
  const themePath = useSelector(getThemePath);
  const isStarterPageTemplate = useSelector(getIsStarterPageTemplate);
  const hasTheme = Boolean(themePath);
  const shouldOpenThemeEditorInSeparateTab = hasTheme && !isStarterPageTemplate;
  const contentId = useSelector(getContentModelId);
  const categoryId = useSelector(getCategoryId);
  const subcategory = useSelector(getSubcategory);
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const canRedoModules = useSelector(getCanRedoModules);
  const canUndoModules = useSelector(getCanUndoModules);
  const canMABExperiment = useSelector(getCanMABExperiment);
  const portalScopesRequest = useSelector(getPortalScopesRequest);
  const portalHasABTesting = useSelector(getPortalHasABTesting);
  const mabCreatableMap = useSelector(getMABCreatableMap);
  const isAnyTestRunning = useSelector(getIsAnyTestRunning);
  const isPersonalized = useSelector(getIsPersonalized);
  const hasReachedMlangPagesLimit = useSelector(getHasReachedMlangPagesLimit);
  const hasThemeSettings = useSelector(getHasThemeSettings);
  const hasDesignTabPermissions = useSelector(getHasDesignTabPermissions);
  const canUseQuickSpot = useSelector(getCanUseQuickSpot);
  const shareablePreviewURL = useSelector(getShareablePreviewUrl);
  const themeName = useSelector(getThemeName);
  const isSitePage = useSelector(getIsSitePage);
  const {
    sourceLanguages
  } = useDeepLSupportedLanguages();
  const canUseAutotranslateForContentLanguage = useSelector(getCanUseAutotranslateForContentLanguage(sourceLanguages));
  const hasContentRemixWriteAccess = useSelector(getHasContentRemixWriteAccess);
  const hasAutotranslateAccess = useSelector(getHasAutotranslateAccess);
  const isStagingV2 = useSelector(getIsStagingV2);
  const hasStagingDomain = useSelector(getHasStagingDomain);
  const isInContentStagingMode = isStagingV2 || hasStagingDomain;
  const settingsDropdownOptions = useSettingsDropdownOptions();
  const isEditingBlogListingPage = categoryId === Categories.blog_listing_page.id;
  const {
    directTranslateExtraProps,
    createMlangVariantExtraProps
  } = useGetLangVariantExtraProps();
  const {
    openUpgradeModal: openABTestUpgradeModal
  } = useUpgradeModal('landing-pages-ab-testing-editor');
  const setOnboardingTourModalOpenWithNavItem = useCallback(navItem => {
    dispatch(updatePatagoniaSettingsModalActiveNavItem(navItem));
    dispatch(openModal(MODAL_TYPES.ONBOARDING_MODAL));
    // dispatch(openModal(MODAL_TYPES.SETTINGS_MODAL));
  }, [dispatch]);
  const openContentRemixModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.CONTENT_REMIX));
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-content-remix-modal'
    });
  }, [dispatch]);
  const openDeleteModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.DELETE_MODAL));
  }, [dispatch]);
  const openCloneModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.CLONE_CONTENT_MODAL));
  }, [dispatch]);
  const openCreatePageFlow = useCallback(() => {
    const portalId = PortalIdParser.get();
    const pageType = isSitePage ? 'site-page' : 'landing-page';
    const pageCreationUrl = `/page-creation/${portalId}/create-page/${pageType}/template?referrer=${Referrers.pageEditor}&referrerUrl=${encodeURIComponent(window.location.href)}`;
    // open the page creation flow in a new tab
    window.open(pageCreationUrl, '_blank');
  }, [isSitePage]);
  const openCreateMlangVariantModal = useCallback(() => {
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-create-multi-lang-variation'
    });
    if (hasReachedMlangPagesLimit) {
      return dispatch(openModal(MODAL_TYPES.MLANG_LIMIT_UPGRADE_MODAL));
    }
    return dispatch(openModal(MODAL_TYPES.CREATE_MLANG_VARIANT));
  }, [dispatch, hasReachedMlangPagesLimit]);
  const openTranslateContentActionModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.TRANSLATE_CONTENT_ACTION_MODAL));
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-direct-translate'
    });
  }, [dispatch]);
  const onThemeClick = useCallback(() => {
    if (shouldOpenThemeEditorInSeparateTab) {
      const params = {
        redirectUrl: window.location.pathname,
        fromAppName: 'pageEditor',
        templatePath
      };
      window.location = ContentRoutes.themeEditor(themePath, params);
    } else {
      dispatch(openPatagoniaSidebar());
      dispatch(setActivePatagoniaPanel(SIDEBAR_PANELS.design));
    }
  }, [shouldOpenThemeEditorInSeparateTab, templatePath, themePath, dispatch]);
  const pageDetailsLink = useMemo(() => {
    return ContentRoutes.details(contentId, categoryId, subcategory, {
      referrer: window.location.href
    });
  }, [categoryId, contentId, subcategory]);
  const handleOpenTestModal = useCallback(() => {
    dispatch(toggleRunTestModal(true));
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-run-a-test'
    });
  }, [dispatch]);
  const showABPQL = requestSucceeded(portalScopesRequest) && !portalHasABTesting;
  const portalLacksMABAccess = mabCreatableMap.get('disabled') && mabCreatableMap.get('reason') === NO_TEST_ACCESS && requestSucceeded(portalScopesRequest);
  const isMabDisabledDueToSmartContent = mabCreatableMap.get('disabled') && mabCreatableMap.get('reason') === SMART_CONTENT;
  const isMabDisabledDueToAbTest = mabCreatableMap.get('disabled') && mabCreatableMap.get('reason') === 'ab';
  const openRunTest = useCallback(testType => {
    if (showABPQL && testType === TEST_MODAL_TYPES.AB_TEST) {
      return openABTestUpgradeModal();
    }
    return handleOpenTestModal();
  }, [showABPQL, handleOpenTestModal, openABTestUpgradeModal]);
  const {
    openPanel: handlePersonalization,
    tooltip: personalizationTooltip,
    isVisible: showPersonalization,
    isDisabled: isPersonalizationDisabled
  } = useContentEditorCreateVariantAction();
  const handleABTest = useCallback(() => {
    dispatch(setTestType(TEST_MODAL_TYPES.AB_TEST));
    openRunTest(TEST_MODAL_TYPES.AB_TEST);
  }, [dispatch, openRunTest]);
  const handleMABTest = useCallback(() => {
    dispatch(setTestType(TEST_MODAL_TYPES.MAB_TEST));
    openRunTest(TEST_MODAL_TYPES.MAB_TEST);
  }, [dispatch, openRunTest]);
  const ABTestPQLWrapper = () => {
    return showABPQL && /*#__PURE__*/_jsx(LockBadge, {
      paddingInlineStart: "200"
    });
  };
  const mabTestDisabledTooltip = useCallback(() => {
    if (isMabDisabledDueToAbTest) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "mab.buttons.mab.disabledTooltip.abTest"
      });
    } else if (isMabDisabledDueToSmartContent) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "mab.buttons.mab.disabledTooltip.smartContent"
      });
    } else if (isAnyTestRunning) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "mab.buttons.mab.disabledTooltip.hasTestRunning"
      });
    }
    return null;
  }, [isMabDisabledDueToAbTest, isMabDisabledDueToSmartContent, isAnyTestRunning]);
  const shouldShowAutoTranslateAction = !isEditingBlogListingPage;
  const shouldDisableTheme = !hasThemeSettings || !hasDesignTabPermissions;
  const themeToolTip = useCallback(() => {
    if (shouldDisableTheme) {
      if (!hasThemeSettings) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "sidebar.tabs.design.disabled.noTheme.message"
        });
      } else if (!hasDesignTabPermissions) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "sidebar.tabs.design.disabled.noPermissions.message"
        });
      }
    }
    return null;
  }, [shouldDisableTheme, hasThemeSettings, hasDesignTabPermissions]);
  const mlangActionItems = useMemo(() => {
    const createMlangVariantAction = Object.assign({
      id: 'createMlangVariant',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.createMlangVariant"
      }),
      onClick: openCreateMlangVariantModal
    }, createMlangVariantExtraProps, canUseQuickSpot && {
      icon: LanguageIcon,
      fullText: I18n.text('editor.header.menus.file.langVariantFullDescription')
    });
    if (shouldShowAutoTranslateAction && canUseAutotranslateForContentLanguage && hasAutotranslateAccess) {
      return [createMlangVariantAction, Object.assign({
        id: 'langVariant',
        text: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "editor.header.menus.file.translateContent"
          }), /*#__PURE__*/_jsx(Flex, {
            children: /*#__PURE__*/_jsx(AIBadge, {})
          })]
        }),
        onClick: openTranslateContentActionModal
      }, directTranslateExtraProps, canUseQuickSpot && {
        icon: LanguageIcon,
        fullText: I18n.text('editor.header.menus.file.translateContentFullDescription')
      })];
    }
    return [createMlangVariantAction];
  }, [openCreateMlangVariantModal, createMlangVariantExtraProps, canUseQuickSpot, shouldShowAutoTranslateAction, canUseAutotranslateForContentLanguage, hasAutotranslateAccess, openTranslateContentActionModal, directTranslateExtraProps]);
  const groups = useMemo(() => [{
    id: 'file',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.file.label"
    }),
    children: [{
      id: 'new',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.createNew"
      }),
      nestedChildren: [Object.assign({
        id: 'page',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.file.createPage"
        }),
        tooltipLabel: isInsideMarketingStudio ? /*#__PURE__*/_jsx(DisabledInStudioMessage, {}) : undefined,
        disableLink: isInsideMarketingStudio,
        onClick: openCreatePageFlow
      }, canUseQuickSpot && {
        icon: FileIcon,
        fullText: I18n.text('editor.header.menus.file.createPageFullDescription')
      }), Object.assign({
        id: 'personalizedVariant',
        text: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "personalizedVariants.title"
          }), /*#__PURE__*/_jsx(Badge, {
            use: "beta",
            marginInlineStart: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "personalizedVariants.beta"
            })
          })]
        }),
        tooltipLabel: isInContentStagingMode ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "contentStaging.tooltips.disabledPersonalization"
        }) : isInsideMarketingStudio ? /*#__PURE__*/_jsx(DisabledInStudioMessage, {}) : personalizationTooltip,
        hideLink: !showPersonalization,
        disableLink: isPersonalizationDisabled || isInsideMarketingStudio || isInContentStagingMode,
        onClick: handlePersonalization
      }, canUseQuickSpot && {
        icon: AudienceTargetingIcon,
        fullText: I18n.text('personalizedVariants.fullText')
      }), ...(shouldShowAutoTranslateAction ? [] : [Object.assign({
        id: 'langVariant',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.file.langVariant"
        }),
        onClick: openCreateMlangVariantModal
      }, createMlangVariantExtraProps, {
        tooltipLabel: isInsideMarketingStudio && !createMlangVariantExtraProps.disableLink ? /*#__PURE__*/_jsx(DisabledInStudioMessage, {}) : undefined,
        disableLink: createMlangVariantExtraProps.disableLink || isInsideMarketingStudio
      }, canUseQuickSpot && {
        icon: LanguageIcon,
        fullText: I18n.text('editor.header.menus.file.langVariantFullDescription')
      })]), Object.assign({
        id: 'abTestVariant',
        text: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: 'editor.header.menus.file.abTestVariant'
          }), /*#__PURE__*/_jsx(ABTestPQLWrapper, {})]
        }),
        onClick: handleABTest,
        hideLink: !canMABExperiment,
        disableLink: isAnyTestRunning || isPersonalized || isInsideMarketingStudio || isInContentStagingMode,
        tooltipLabel: isInContentStagingMode ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "contentStaging.tooltips.disabledTests"
        }) : isAnyTestRunning ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'mab.buttons.mab.disabledTooltip.hasTestRunning'
        }) : isInsideMarketingStudio ? /*#__PURE__*/_jsx(DisabledInStudioMessage, {}) : undefined
      }, canUseQuickSpot && {
        icon: TestIcon,
        fullText: I18n.text('editor.header.menus.file.abTestVariant')
      }), Object.assign({
        id: 'mabTestVariant',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'editor.header.menus.file.mabTestVariant'
        }),
        onClick: handleMABTest,
        hideLink: !canMABExperiment || portalLacksMABAccess,
        disableLink: isAnyTestRunning || isMabDisabledDueToSmartContent || isMabDisabledDueToAbTest || isPersonalized || isInsideMarketingStudio || isInContentStagingMode,
        tooltipLabel: isInContentStagingMode ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "contentStaging.tooltips.disabledTests"
        }) : mabTestDisabledTooltip() || isInsideMarketingStudio && /*#__PURE__*/_jsx(DisabledInStudioMessage, {})
      }, canUseQuickSpot && {
        icon: TestIcon,
        fullText: I18n.text('editor.header.menus.file.mabTestVariant')
      })]
    }, ...(shouldShowAutoTranslateAction && mlangActionItems.some(mlangActionItem => !mlangActionItem.hideLink) ? [{
      id: 'multiLanguage',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.multiLanguage"
      }),
      disableLink: isInsideMarketingStudio,
      tooltipLabel: isInsideMarketingStudio ? /*#__PURE__*/_jsx(DisabledInStudioMessage, {}) : undefined,
      nestedChildren: mlangActionItems
    }] : []), Object.assign({
      id: 'clonePage',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.clone"
      }),
      disableLink: isEditingBlogListingPage || isInsideMarketingStudio,
      tooltipLabel: isEditingBlogListingPage && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.disabledActions.CLONE.blogListingPage"
      }) || isInsideMarketingStudio && /*#__PURE__*/_jsx(DisabledInStudioMessage, {}),
      onClick: openCloneModal
    }, canUseQuickSpot && {
      icon: DuplicateIcon,
      fullText: I18n.text('editor.header.menus.file.clone')
    }), Object.assign({
      id: 'deletePage',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.delete"
      }),
      disableLink: isEditingBlogListingPage || isInsideMarketingStudio,
      tooltipLabel: isEditingBlogListingPage && ( /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.disabledActions.DELETE.cannotDeletePrimary"
      }) || isInsideMarketingStudio && /*#__PURE__*/_jsx(DisabledInStudioMessage, {})),
      onClick: openDeleteModal
    }, canUseQuickSpot && {
      icon: DeleteIcon,
      fullText: I18n.text('editor.header.menus.file.delete')
    }), ...(hasContentRemixWriteAccess && (categoryId === Categories.landing_page.id || categoryId === Categories.site_page.id) ? [Object.assign({
      id: 'remixContent',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.remixContent"
      }),
      tooltipLabel: isInsideMarketingStudio ? /*#__PURE__*/_jsx(DisabledInStudioMessage, {}) : undefined,
      disableLink: isInsideMarketingStudio,
      onClick: openContentRemixModal
    }, canUseQuickSpot && {
      icon: ArtificialIntelligenceIcon,
      fullText: I18n.text('editor.header.menus.file.remixContent')
    })] : [])]
  }, {
    id: 'edit',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.edit.label"
    }),
    children: [Object.assign({
      id: 'undo',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.undo"
      }),
      onClick: () => dispatch(undoModuleReducerChange()),
      disableLink: !canUndoModules || isReadOnlyMode
    }, canUseQuickSpot && {
      icon: UndoIcon,
      fullText: I18n.text('editor.header.menus.edit.undo')
    }), Object.assign({
      id: 'redo',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.redo"
      }),
      onClick: () => dispatch(redoModuleReducerChange()),
      disableLink: !canRedoModules || isReadOnlyMode
    }, canUseQuickSpot && {
      icon: RedoIcon,
      fullText: I18n.text('editor.header.menus.edit.redo')
    }), Object.assign({
      id: 'editTheme',
      text: themeName ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.themeWithName",
        options: {
          themeName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.theme"
      }),
      onClick: onThemeClick,
      disableLink: shouldDisableTheme || isInsideMarketingStudio,
      tooltipLabel: themeToolTip() || isInsideMarketingStudio && /*#__PURE__*/_jsx(DisabledInStudioMessage, {})
    }, canUseQuickSpot && {
      icon: EditIcon,
      fullText: themeName ? I18n.text('editor.header.menus.edit.themeWithNameFullDescription', {
        themeName
      }) : I18n.text('editor.header.menus.edit.themeFullDescription')
    })]
  }, {
    id: 'view',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.view.label"
    }),
    children: [Object.assign({
      id: 'versionHistory',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.view.versionHistory"
      }),
      onClick: () => dispatch(routeToPage('revisions'))
    }, canUseQuickSpot && {
      icon: ViewIcon,
      fullText: I18n.text('editor.header.menus.view.versionHistory')
    }), Object.assign({
      id: 'pageDetails',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.view.details"
      }),
      href: pageDetailsLink,
      external: true
    }, canUseQuickSpot && {
      icon: ViewIcon,
      fullText: I18n.text('editor.header.menus.view.details')
    }), Object.assign({
      id: 'preview',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.view.preview"
      }),
      href: shareablePreviewURL,
      external: true,
      hideLink: !shareablePreviewURL
    }, canUseQuickSpot && {
      icon: ViewIcon,
      fullText: I18n.text('editor.header.menus.view.preview')
    })]
  }, {
    id: 'settings',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.settings.label"
    }),
    children: settingsDropdownOptions
  }, {
    id: 'help',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.help.label"
    }),
    children: [Object.assign({
      id: 'takeTour',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.help.takeTour"
      }),
      onClick: () => setOnboardingTourModalOpenWithNavItem('intropage')
    }, canUseQuickSpot && {
      icon: QuestionIcon,
      fullText: I18n.text('editor.header.menus.help.takeTour')
    }), Object.assign({
      id: 'knowledgeBase',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.help.knowledgeBase"
      }),
      href: 'https://knowledge.hubspot.com/get-started/build-your-website',
      external: true
    }, canUseQuickSpot && {
      icon: QuestionIcon,
      fullText: I18n.text('editor.header.menus.help.knowledgeBase')
    })]
  }], [isInsideMarketingStudio, openCreatePageFlow, canUseQuickSpot, personalizationTooltip, showPersonalization, isPersonalizationDisabled, handlePersonalization, isInContentStagingMode, shouldShowAutoTranslateAction, openCreateMlangVariantModal, createMlangVariantExtraProps, canMABExperiment, portalLacksMABAccess, isAnyTestRunning, isPersonalized, handleABTest, handleMABTest, isMabDisabledDueToSmartContent, isMabDisabledDueToAbTest, mabTestDisabledTooltip, mlangActionItems, isEditingBlogListingPage, openCloneModal, openDeleteModal, hasContentRemixWriteAccess, categoryId, openContentRemixModal, canUndoModules, isReadOnlyMode, canRedoModules, themeName, onThemeClick, shouldDisableTheme, themeToolTip, pageDetailsLink, shareablePreviewURL, settingsDropdownOptions, dispatch, setOnboardingTourModalOpenWithNavItem]);
  return groups;
};